import {
  type DocumentSearchMeta,
  searchDocumentsQueryOptions,
} from "@prismicio/editor-fields"
import { useDebounceState } from "@prismicio/editor-support/React"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { createContext, type RefObject, useContext } from "react"

import { staticEditorConfig } from "../../app/editorConfig"
import type { DocumentVersionStatus } from "../../app/services/core"
import { useRepository } from "../../app/services/repository"
import { trackDocumentSearchInDialog } from "../../app/services/tracking"
import type { Filter } from "../DocumentListBody/ActionBar/FilterDropdown"
import { useDocumentListLocales } from "../useDocumentListLocales"

export interface SearchDialogContext {
  filters: {
    searchTerm: string
    customType: Filter[]
    tag: Filter[]
    authors: Filter[]
    status: Filter<DocumentVersionStatus>[]
    hasFiltersApplied: boolean
  }
  loading: boolean
  documents: DocumentSearchMeta[]
  tagFilterDropdownRef: RefObject<HTMLDivElement>
  typeFilterDropdownRef: RefObject<HTMLDivElement>
  statusFilterDropdownRef: RefObject<HTMLDivElement>
  authorFilterDropdownRef: RefObject<HTMLDivElement>
}

export const SearchDialogContext = createContext<SearchDialogContext>({
  loading: false,
  filters: {
    searchTerm: "",
    customType: [],
    tag: [],
    authors: [],
    status: [],
    hasFiltersApplied: false,
  },
  documents: [],
  tagFilterDropdownRef: { current: null },
  typeFilterDropdownRef: { current: null },
  statusFilterDropdownRef: { current: null },
  authorFilterDropdownRef: { current: null },
})

export function useSearchDialogContext() {
  return useContext(SearchDialogContext)
}

const searchDebounceMs = 300

interface UseDocumentSearchProps {
  filters: SearchDialogContext["filters"]
}

export function useDocumentSearch(props: UseDocumentSearchProps) {
  const { filters } = props

  const { locale } = useDocumentListLocales()

  const { debouncedValue: debouncedFilters } = useDebounceState({
    state: filters,
    ms: searchDebounceMs,
    synchronize: true,
  })

  const { storageVersion } = useRepository()

  return useQuery({
    ...searchDocumentsQueryOptions({
      searchTerm: debouncedFilters.searchTerm,
      tags: debouncedFilters.tag.map((tag) => tag.value),
      customTypes: debouncedFilters.customType.map((type) => type.value),
      locale,
      status: debouncedFilters.status.map((status) => status.value),
      authors: debouncedFilters.authors.map((author) => author.value),
      storageVersion,
      config: staticEditorConfig,
      track: trackDocumentSearchInDialog,
    }),
    placeholderData: keepPreviousData,
    staleTime: 0,
    enabled: debouncedFilters.hasFiltersApplied,
  })
}
